/**
 * PubFinderService
 *
 * Finds nearby pubs using OpenStreetMap's Overpass API
 * This is a free, open-source alternative to Google Places API
 *
 * Searches for:
 * - Pubs (amenity=pub)
 * - Bars (amenity=bar)
 * - Beer gardens (amenity=biergarten)
 */

import { buildAroundQuery, runOverpassQuery, toVenuesByDistance } from './overpass';
import { formatAddress } from '../utils/geo';

const SEARCH_RADIUS = 2000; // Search within 2km radius
const PUB_FILTERS = [
  ['amenity', 'pub'],
  ['amenity', 'bar'],
  ['amenity', 'biergarten'],
];

/**
 * Find nearby pubs within a radius
 * @param {number} latitude - User's latitude
 * @param {number} longitude - User's longitude
 * @param {number} radius - Search radius in meters (default: 2000m = 2km)
 * @returns {Promise<Array>} Pubs sorted by distance (empty if none nearby)
 * @throws {VenueLookupError} When the Overpass API can't be reached
 */
export const findNearbyPubs = async (latitude, longitude, radius = SEARCH_RADIUS) => {
  const query = buildAroundQuery(PUB_FILTERS, radius, latitude, longitude);
  const data = await runOverpassQuery(query);

  return toVenuesByDistance(data.elements, latitude, longitude, (tags) => ({
    name: tags.name || 'Unnamed Pub',
    type: tags.amenity || 'pub',
    address: formatAddress(tags),
    openingHours: tags.opening_hours,
    phone: tags.phone,
    website: tags.website,
  }));
};

/**
 * Get the closest pub from a list
 */
export const getClosestPub = (pubs) => {
  if (!pubs || pubs.length === 0) return null;
  return pubs[0]; // Already sorted by distance
};
